// External libraries
import { useRecoilValue, useSetRecoilState } from 'recoil';

// Components
import { cifState, POICategories, spinnerState, userCurrencyState } from "../states";

// Utilities
import { useFetchWrapper } from '../helpers';
import { APIS, CATEGORY_ORDER_IN_DROPDOWN } from '../constants';


const useCIFService = () => {
    const fetchWrapper = useFetchWrapper();
    const setDashboardData = useSetRecoilState(cifState);
    const setPoiCategories = useSetRecoilState(POICategories);
    const SetSpinner = useSetRecoilState(spinnerState);
    const selCurrency = useRecoilValue(userCurrencyState);

    const getInOutFlowData = (geoCode: number) => {
        //SetSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        const currency = localStorage.getItem('currency');
        return fetchWrapper.get(`${APIS.CIF.GET_INOUTFLOW_DATA}?geo-code=${geoCode}&currency=${currency}`).then(data => {
            setDashboardData(prevCIFData => ({
                ...prevCIFData,
                inOutFlowData: data
            }));
            //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        }).catch(error => {
            //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        });
    }

    const getEHGrowthData = (geoCode: number) => {
        // //SetSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        const currency = localStorage.getItem('currency');
        return fetchWrapper.get(`${APIS.CIF.GET_EH_GROWTH_DATA}?geo-code=${geoCode}&currency=${currency}`).then(data => {
            setDashboardData(prevCIFData => ({
                ...prevCIFData,
                ehGrowthGraphData: data
            }));
            // //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        }).catch(error => {
            // //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        });
    }

    const getMetricBreakdownData = (geoCode: number) => {
        // //SetSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        const currency = localStorage.getItem('currency');
        return fetchWrapper.get(`${APIS.CIF.GET_METRIC_BREAKDOWN}?geo-code=${geoCode}&currency=${currency}`).then(data => {
            setDashboardData(prevCIFData => ({
                ...prevCIFData,
                metricBreakdownInfo: data
            }));
            // //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        }).catch(error => {
            // //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        });
    }

    const getGeoSpecificData = (geoCode: number) => {
        // //SetSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        const currency = localStorage.getItem('currency');
        return fetchWrapper.get(`${APIS.CIF.GET_GEO_SPECIFIC_DATA}?geo-code=${geoCode}&currency=${currency}`).then(data => {
            setDashboardData(prevCIFData => ({
                ...prevCIFData,
                geoInfo: data
            }));
            // //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        }).catch(error => {
            // //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        });
    }

    const getCoreSolutionsGraphData = (geoCode: number) => {
        //SetSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        const currency = localStorage.getItem('currency');
        return fetchWrapper.get(`${APIS.CIF.GET_CORE_SOLUTIONS_DATA}?geo-code=${geoCode}&currency=${currency}`).then(data => {
            setDashboardData(prevCIFData => ({
                ...prevCIFData,
                coreSolutionsData: data
            }));
            //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        }).catch(error => {
            //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        });
    }

    const sendEmail = (payload: any) => {
        return fetchWrapper.post(APIS.CIF.SEND_EMAIL, payload);
    }

    const getPOICategoryList = async () => {
        //SetSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        try {
            const response = await fetchWrapper.get(`${APIS.CIF.GET_POI_CATEGORIES}`);
            const catData = response.sort((a: any, b: any) => CATEGORY_ORDER_IN_DROPDOWN.indexOf(a.name) - CATEGORY_ORDER_IN_DROPDOWN.indexOf(b.name));
            setPoiCategories(catData);
            //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        } catch (error) {
            //SetSpinner(prev => ({ ...prev, count: prev.count - 1 }));
        }
    }

    const getBasicCif = (geoCode: number) => {
        return fetchWrapper.get(`${APIS.CIF.GET_BASIC_CIF}?geo-code=${geoCode}&currency=${selCurrency}`);
    }

    const getCifCtvData = (geoCode: number) => {
        return fetchWrapper.get(`${APIS.CIF.GET_CIF_CTV}?geo-code=${geoCode}`);
    }

    const getCifPoiData = (payload: any) => {
        return fetchWrapper.post(APIS.CIF.GET_POI_CAT_DETAILS, payload);
    }

    const getPoiByYear = (payload: any) => {
        return fetchWrapper.post(APIS.CIF.GET_POI_BY_YEAR, payload);
    }

    const getMyPresenceConfig = (geoCode: number) => {
        return fetchWrapper.get(`${APIS.CIF.GET_MY_PRESENCE_CONFIG}?geocode=${geoCode}`);
    }

    const getMyPresence = (payload: any) => {
        return fetchWrapper.post(APIS.CIF.GET_MBP_V2, payload);
    }

    const getOverlayFilters = (geoCode: number, subLevel: number | null) => {
        return fetchWrapper.get(`${APIS.CIF.GET_OVERLAY_FILTERS}?geocode=${geoCode}${subLevel ? `&sub_level=${subLevel}` : ''}`);
    }
    
    const getAllFilterSliders = async (overlays: any[], geoCode: number, currency: string, subLevel: number | null) => {
        const promises = overlays
            .filter(overlay => !overlay.isdisable)
            .map(overlay =>
                getSliderOverlay(geoCode, overlay.key, currency, subLevel)
                    .then(data => data)
                    .catch(error => {
                        console.error(`Error fetching slider for overlay ${overlay.key}:`, error);
                    })
            );
    
        const results = await Promise.allSettled(promises);
    
        // Flatten the results (since each promise already returns an object with status)
        return results
            .filter(r => r.status === 'fulfilled')
            .map(r => (r as PromiseFulfilledResult<any>).value);
    };

    const getSliderOverlay = (geoCode: number, key: string, currency: string, subLevel: number | null) => {
        return fetchWrapper.get(`${APIS.CIF.GET_SLIDER_DATA}?geocode=${geoCode}&key=${key}&currency=${currency}${subLevel ? `&sub_level=${subLevel}` : ''}`);
    }

    const getFilteredDistricts = (payload: any) => {
        return fetchWrapper.post(APIS.CIF.GET_FILTERED_DISTRICTS, payload);
    }

     const getAlllocations = () => {
        return fetchWrapper.get(APIS.COMPARE.GET_ALL_LOCATIONS);
    }

    return {
        getInOutFlowData,
        getEHGrowthData,
        getMetricBreakdownData,
        getGeoSpecificData,
        getCoreSolutionsGraphData,
        sendEmail,
        getPOICategoryList,
        getBasicCif,
        getCifCtvData,
        getCifPoiData,
        getPoiByYear,
        getMyPresenceConfig,
        getMyPresence,
        getOverlayFilters,
        getSliderOverlay,
        getFilteredDistricts,
        getAllFilterSliders,
        getAlllocations
    }
}

export { useCIFService };